/**
 * Duration helpers.
 *
 * Plan D9: staff enter `[ 2 ] hours [ 30 ] minutes`; the stored contract is a single
 * integer of minutes (§1.13). The conversion lives here rather than in the form so
 * the API, the tests and any future importer agree with the UI by construction —
 * and so the "= 150 minutes" echo beneath the inputs cannot drift from what is sent.
 */
export const MINUTES_PER_HOUR = 60;
/** `(2, 30)` -> `150`. Both parts must be whole, non-negative numbers. */
export function toMinutes(hours, minutes) {
    if (!Number.isInteger(hours) || !Number.isInteger(minutes)) {
        throw new RangeError('Hours and minutes must be whole numbers.');
    }
    if (hours < 0 || minutes < 0) {
        throw new RangeError('Hours and minutes cannot be negative.');
    }
    return hours * MINUTES_PER_HOUR + minutes;
}
/** `150` -> `{ hours: 2, minutes: 30 }`, for populating the edit form. */
export function fromMinutes(totalMinutes) {
    const safe = Math.max(0, Math.trunc(totalMinutes));
    return {
        hours: Math.floor(safe / MINUTES_PER_HOUR),
        minutes: safe % MINUTES_PER_HOUR,
    };
}
/** `150` -> `'2h 30m'`. For tables, where the raw number reads poorly. */
export function formatDuration(totalMinutes) {
    const { hours, minutes } = fromMinutes(totalMinutes);
    if (hours === 0)
        return `${minutes}m`;
    if (minutes === 0)
        return `${hours}h`;
    return `${hours}h ${minutes}m`;
}
/**
 * `150` -> `'2 hours 30 minutes'`. For prose, where an abbreviation reads badly.
 *
 * Added for the recognition certificate, which is the first place a duration
 * appears in a sentence rather than in a cell — "2h 30m of volunteer service"
 * is fine in a table and wrong on a document somebody frames. It lives here
 * with the others so a certificate and the screen that offered it cannot round
 * the same number differently.
 *
 * Singular where it matters: `60` -> `'1 hour'`, `61` -> `'1 hour 1 minute'`.
 */
export function formatDurationWords(totalMinutes) {
    const { hours, minutes } = fromMinutes(totalMinutes);
    const parts = [];
    if (hours > 0)
        parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
    if (minutes > 0)
        parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
    // Zero is a real input here only if something upstream failed to guard it —
    // a certificate for no time cannot be issued — so it says so plainly rather
    // than returning an empty string that would render as a gap in a sentence.
    return parts.length > 0 ? parts.join(' ') : '0 minutes';
}
//# sourceMappingURL=duration.js.map